import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors, fonts } from '../style/GlobalStyles';

const Row = styled.div`
  border-top: 1px solid ${Colors.line};

  &:last-child {
    border-bottom: 1px solid ${Colors.line};
  }
`;

const RowHead = styled.button`
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  display: grid;
  grid-template-columns: 220px 1fr 30px;
  align-items: center;
  gap: 2rem;
  padding: 2rem 0.5rem;
  transition: padding-left 0.3s ease;

  &:hover {
    padding-left: 1.2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 24px;
    gap: 0.5rem 1rem;
    padding: 1.5rem 0.25rem;
  }
`;

const Category = styled.div`
  font-family: ${fonts.body};
  font-size: 0.95rem;
  color: ${Colors.muted};

  small {
    display: block;
    font-size: 0.8rem;
    opacity: 0.7;
    margin-top: 0.2rem;
  }

  @media (max-width: 768px) {
    grid-row: 2;
    grid-column: 1 / -1;
  }
`;

const Name = styled.div`
  font-family: ${fonts.display};
  text-transform: uppercase;
  font-size: clamp(1.5rem, 4vw, 2.6rem);
  color: ${Colors.ink};
  letter-spacing: 0.5px;
  line-height: 1;
`;

const Bullet = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $open }) => ($open ? Colors.lavender : Colors.ink)};
  justify-self: end;
  transition: background 0.3s ease, transform 0.3s ease;
  transform: ${({ $open }) => ($open ? 'scale(1.3)' : 'scale(1)')};
`;

const Body = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.5s cubic-bezier(0.65, 0, 0.35, 1);
`;

const BodyInner = styled.div`
  overflow: hidden;
`;

const BodyContent = styled.div`
  display: flex;
  gap: 2.5rem;
  padding: 0 0.5rem 3rem 0.5rem;

  @media (max-width: 880px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const VideoWrap = styled.div`
  flex: 1.4;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: ${Colors.ink};
`;

const Vid = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Meta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${Colors.ink};
  border: 1px solid ${Colors.ink};
  border-radius: 999px;
  padding: 0.35rem 1rem;
`;

const Desc = styled.p`
  font-family: ${fonts.body};
  font-size: 1rem;
  line-height: 1.7;
  color: ${Colors.muted};
  margin: 0;
`;

const CardProjects = ({ id, nombre, tags = [], video, desc, category, open, onToggle }) => {
  // Monta el video la primera vez que se abre la fila (lazy) y lo mantiene montado.
  const [loaded, setLoaded] = useState(open);

  useEffect(() => {
    if (open) setLoaded(true);
  }, [open]);

  return (
    <Row>
      <RowHead onClick={onToggle} aria-expanded={open}>
        <Category>
          {category}
          <small>Project {id}</small>
        </Category>
        <Name>{nombre}</Name>
        <Bullet $open={open} />
      </RowHead>
      <Body $open={open}>
        <BodyInner>
          <BodyContent>
            <VideoWrap>
              {loaded && (
                <Vid autoPlay muted loop playsInline preload="metadata">
                  <source src={video} type="video/mp4" />
                </Vid>
              )}
            </VideoWrap>
            <Meta>
              <Tags>
                {tags.map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </Tags>
              <Desc>{desc}</Desc>
            </Meta>
          </BodyContent>
        </BodyInner>
      </Body>
    </Row>
  );
};

export default CardProjects;
